// src/components/AnalyticsDashboardModal/AnalyticsDashboardModal.js

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styles from './AnalyticsDashboardModal.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faChartBar,
  faChartLine,
  faChartPie,
  faSyncAlt,
  faExternalLinkAlt,
  faWallet,
  faExchangeAlt,
  faCreditCard,
  faRobot,
  faCogs,
  faUser,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { useData } from '../context/DataContext';
import { Line, Bar, Pie } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  TimeScale,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  TimeScale,
);

const formatDateForInput = (date) => date.toISOString().split('T')[0];

const formatToLocalTime = (dateString) => {
  if (!dateString) return { date: '-', time: '-' };

  // Ensure UTC interpretation
  const utcDateString = dateString.endsWith('Z')
    ? dateString
    : `${dateString}Z`;
  const dateObj = new Date(utcDateString);

  return {
    // Result: "Mar 28, 2026"
    date: dateObj.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }),
    // Result: "10:20 AM"
    time: dateObj.toLocaleTimeString('en-US', {
      hour: 'numeric', // 'numeric' instead of '2-digit' removes leading zero (e.g. 9:00 vs 09:00)
      minute: '2-digit',
      hour12: true,
    }),
  };
};

const getDatesForTimeFrame = (timeFrame) => {
  const endDate = new Date();
  const startDate = new Date();
  switch (timeFrame) {
    case 'today':
      break;
    case 'last7days':
      startDate.setDate(endDate.getDate() - 6);
      break;
    case 'this_month':
      startDate.setDate(1);
      break;
    case 'last30days':
      startDate.setDate(endDate.getDate() - 29);
      break;
    default:
      return null;
  }
  return {
    start_date: formatDateForInput(startDate),
    end_date: formatDateForInput(endDate),
  };
};

const chartIconMap = {
  line: faChartLine,
  bar: faChartBar,
  stacked_bar: faChartBar,
  pie: faChartPie,
};
const toTitleCase = (str) => {
  if (!str) return '';
  return str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const AnalyticsDashboardModal = () => {
  const {
    isAnalyticsModalOpen,
    setIsAnalyticsModalOpen,
    analyticsDashboardLoading,
    analyticsDashboardError,
    analyticsDashboardData,
    analyticsInitialTab,
    isStripeLoading,
    lastAnalyticsQuery,
    fetchAnalyticsData,
    analyticsSelectedChartType,
    setAnalyticsSelectedChartType,
    userInvoices,
    fetchUserInvoices,
    userProfile,
    retrieveUserProfile,
    companyWalletTransactions,
    fetchCompanyWalletTransactions,
    handleStripeCheckout,
    handleStripeSetup,
    loadAutoTopupConfig,
    saveAutoTopupConfig,
    autoTopupConfig,
  } = useData();

  const walletBalance =
    userProfile?.walletBalance !== undefined ? userProfile.walletBalance : 0;
  const isAdmin = userProfile?.role === 'Admin';

  const [activeTab, setActiveTab] = useState('usage');
  const [isMounted, setIsMounted] = useState(false);
  const [metric, setMetric] = useState('total_cost');
  const [primaryDimension, setPrimaryDimension] = useState('analysis_type');
  const [showSecondary, setShowSecondary] = useState(false);
  const [secondaryDimension, setSecondaryDimension] = useState('');
  const [timeFrame, setTimeFrame] = useState('last7days');
  const [customTimeRange, setCustomTimeRange] = useState({
    start_date: '',
    end_date: '',
  });
  const [timeGrouping, setTimeGrouping] = useState('day');
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Top-Up Sub-Tabs State
  const [topUpMode, setTopUpMode] = useState('quick'); // 'quick', 'custom', 'auto'
  const [customAmount, setCustomAmount] = useState('');

  // Auto-Pilot Form State
  const [autoEnabled, setAutoEnabled] = useState(false);
  const [autoThreshold, setAutoThreshold] = useState(50);
  const [autoAmount, setAutoAmount] = useState(100);
  const [autoMaxDaily, setAutoMaxDaily] = useState(1);

  // =========================================================
  // SINGLE MASTER USE-EFFECT: Mount, Redirects, and Defaults
  // =========================================================
  useEffect(() => {
    if (isAnalyticsModalOpen) {
      const timer = setTimeout(() => setIsMounted(true), 10);

      // 1. Sync local tab with the intended tab from DataContext
      setActiveTab(analyticsInitialTab || 'usage');

      if (analyticsInitialTab === 'topup') {
        loadAutoTopupConfig(); // Fetch card data if we landed on Top-Up
      } else {
        // 2. Standard Open (Usage Tab Defaults)
        setTimeFrame('this_month');
        setTimeGrouping('day');

        if (lastAnalyticsQuery) {
          setMetric(lastAnalyticsQuery.metric || 'total_cost');
          setPrimaryDimension(lastAnalyticsQuery.primary_dimension || '');
          setShowSecondary(!!lastAnalyticsQuery.secondary_dimension);
          setSecondaryDimension(lastAnalyticsQuery.secondary_dimension || '');
        } else {
          setMetric('total_cost');
          setPrimaryDimension('analysis_type');
          setShowSecondary(false);
        }
        const defaultDates = getDatesForTimeFrame('this_month');
        setCustomTimeRange({
          start_date: defaultDates.start_date,
          end_date: defaultDates.end_date,
        });
      }

      return () => clearTimeout(timer);
    } else {
      setIsMounted(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAnalyticsModalOpen]); // STRICTLY ONLY RUN WHEN MODAL OPENS/CLOSES

  // Sync Auto-Pilot local state with Fetched Context
  useEffect(() => {
    if (autoTopupConfig?.config) {
      setAutoEnabled(autoTopupConfig.config.auto_topup_enabled);
      setAutoThreshold(
        parseFloat(autoTopupConfig.config.auto_topup_threshold_usd) || 50,
      );
      setAutoAmount(
        parseFloat(autoTopupConfig.config.auto_topup_amount_usd) || 100,
      );
      setAutoMaxDaily(autoTopupConfig.config.auto_topup_max_daily || 1);
    }
  }, [autoTopupConfig]);

  const visibleTotal = useMemo(() => {
    const datasets = analyticsDashboardData?.data?.datasets;
    if (!datasets || !Array.isArray(datasets) || datasets.length === 0)
      return 0;
    return datasets.reduce(
      (acc, ds) =>
        acc +
        (ds.data || []).reduce(
          (inner, val) => inner + (parseFloat(val) || 0),
          0,
        ),
      0,
    );
  }, [analyticsDashboardData]);

  const unsupportedState = useMemo(() => {
    let isMultiPointTrend = false;
    if (
      timeGrouping === 'day' &&
      timeFrame !== 'today' &&
      timeFrame !== 'custom'
    )
      isMultiPointTrend = true;
    if (timeGrouping === 'week' && timeFrame === 'last30days')
      isMultiPointTrend = true;
    if (timeGrouping === 'month' && timeFrame === 'custom')
      isMultiPointTrend = true;
    if (
      timeFrame === 'custom' &&
      customTimeRange.start_date !== customTimeRange.end_date
    )
      isMultiPointTrend = true;

    const isUnsupported =
      (showSecondary && !!secondaryDimension && isMultiPointTrend) ||
      (metric === 'cost_breakdown' &&
        primaryDimension !== '' &&
        isMultiPointTrend) ||
      (metric === 'cost_breakdown' && showSecondary && !!secondaryDimension);

    return {
      isUnsupported,
      message: isUnsupported
        ? "Please select 'None' in the 'by' option to see a snapshot instead."
        : null,
    };
  }, [
    timeGrouping,
    showSecondary,
    primaryDimension,
    secondaryDimension,
    timeFrame,
    customTimeRange.start_date,
    customTimeRange.end_date,
    metric,
  ]);

  const handleTimeFrameChange = (newTimeFrame) => {
    setTimeFrame(newTimeFrame);
    if (newTimeFrame === 'today' || newTimeFrame === 'custom')
      setTimeGrouping('');
    else if (!timeGrouping) setTimeGrouping('day');
  };

  useEffect(() => {
    if (!isAnalyticsModalOpen) return;
    if (activeTab === 'usage') {
      if (unsupportedState.isUnsupported || !metric) return;
      let finalTimeRange = customTimeRange;
      if (timeFrame !== 'custom')
        finalTimeRange = getDatesForTimeFrame(timeFrame);
      if (!finalTimeRange.start_date || !finalTimeRange.end_date) return;

      fetchAnalyticsData({
        time_range: finalTimeRange,
        metric,
        time_grouping: timeGrouping || null,
        primary_dimension: primaryDimension || null,
        secondary_dimension: showSecondary ? secondaryDimension || null : null,
      });
    } else if (activeTab === 'history') {
      fetchUserInvoices();
    } else if (activeTab === 'transactions') {
      fetchCompanyWalletTransactions();
    } else if (activeTab === 'topup') {
      loadAutoTopupConfig();
    }
    setAnalyticsSelectedChartType(null);
  }, [
    isAnalyticsModalOpen,
    activeTab,
    metric,
    primaryDimension,
    secondaryDimension,
    showSecondary,
    timeFrame,
    customTimeRange,
    timeGrouping,
  ]);

  const handleClose = () => setIsAnalyticsModalOpen(false);
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') handleClose();
  }, []);

  useEffect(() => {
    if (isAnalyticsModalOpen)
      document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isAnalyticsModalOpen, handleKeyDown]);

  const chartToRender =
    analyticsSelectedChartType ||
    analyticsDashboardData?.recommended_chart_type;
  const chartOptions = useMemo(() => {
    const axisType = timeGrouping === 'day' ? 'time' : 'category';
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { position: 'top', labels: { color: '#e0e0e0' } } },
      scales: {
        x: {
          type: axisType,
          ...(axisType === 'time' && {
            time: { unit: 'day', displayFormats: { day: 'EEE, dd MMM' } },
          }),
          stacked: chartToRender === 'stacked_bar',
          ticks: { color: '#a0a0a0' },
          grid: { color: 'rgba(255, 255, 255, 0.1)' },
        },
        y: {
          stacked: chartToRender === 'stacked_bar',
          ticks: { color: '#a0a0a0' },
          grid: { color: 'rgba(255, 255, 255, 0.1)' },
        },
      },
    };
  }, [chartToRender, timeGrouping]);

  const COLOR_PALETTE = [
    '#7CB5EC',
    '#F7A35C',
    '#90EE7E',
    '#AAEEEE',
    '#f53f88ff',
    '#EEAAEE',
    '#55BF3B',
    '#dd5f5fff',
    '#7798BF',
    '#AAEEEE',
  ];

  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    await retrieveUserProfile();
    setIsRefreshing(false);
  };

  const renderTopUp = () => (
    <div className={styles.topUpContainer}>
      <div
        className={`${styles.walletBalanceCard} ${walletBalance > 10 ? styles.balanceHealthy : walletBalance > 0 ? styles.balanceWarning : styles.balanceCritical}`}
        style={{
          width: '100%',
          maxWidth: '400px',
          margin: '0 auto 1.5rem',
          position: 'relative',
        }}
      >
        <button
          onClick={handleManualRefresh}
          className={styles.inlineRefreshBtn}
          title="Refresh Balance"
        >
          <FontAwesomeIcon icon={faSyncAlt} spin={isRefreshing} />
        </button>
        <div className={styles.walletBalanceLabel}>
          Available Workspace Balance
        </div>
        <div className={styles.walletBalanceValue}>
          ${walletBalance.toFixed(2)}
        </div>
      </div>

      {/* Segmented Controls */}
      <div className={styles.topUpSegmentedControl}>
        <button
          className={`${styles.segmentBtn} ${topUpMode === 'quick' ? styles.active : ''}`}
          onClick={() => setTopUpMode('quick')}
        >
          Packages
        </button>
        <button
          className={`${styles.segmentBtn} ${topUpMode === 'custom' ? styles.active : ''}`}
          onClick={() => setTopUpMode('custom')}
        >
          Custom Amount
        </button>
        <button
          className={`${styles.segmentBtn} ${topUpMode === 'auto' ? styles.active : ''}`}
          onClick={() => setTopUpMode('auto')}
        >
          <FontAwesomeIcon icon={faRobot} /> Auto-Pilot
        </button>
      </div>

      {/* Mode 1: Quick Add */}
      {topUpMode === 'quick' && (
        <div className={styles.topUpGrid}>
          {[100, 200, 500, 1000].map((amount) => (
            <button
              key={amount}
              onClick={() => handleStripeCheckout(amount)}
              disabled={isStripeLoading}
              className={styles.topUpActionButton}
            >
              {isStripeLoading ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                `Add $${amount}`
              )}
            </button>
          ))}
        </div>
      )}

      {/* Mode 2: Custom Amount */}
      {topUpMode === 'custom' && (
        <div className={styles.customAmountPanel}>
          <div className={styles.currencyInputWrapper}>
            <span className={styles.currencySymbol}>$</span>
            <input
              type="number"
              className={styles.customInput}
              value={customAmount}
              onChange={(e) => setCustomAmount(e.target.value)}
              placeholder="150"
              min="50"
              max="2000"
            />
          </div>
          <p style={{ color: '#888', fontSize: '0.85em', margin: 0 }}>
            Minimum $50. Maximum $2,000.
          </p>
          <button
            className={styles.customSubmitBtn}
            disabled={
              isStripeLoading ||
              !customAmount ||
              parseFloat(customAmount) < 50 ||
              parseFloat(customAmount) > 2000
            }
            onClick={() => handleStripeCheckout(parseFloat(customAmount))}
          >
            {isStripeLoading ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              'Proceed to Checkout'
            )}
          </button>
        </div>
      )}

      {/* Mode 3: Auto-Pilot */}
      {topUpMode === 'auto' && (
        <div className={styles.autoPilotCard}>
          <div className={styles.formGroup}>
            <label>Enable Auto-Pilot</label>
            <label className={styles.switch}>
              <input
                type="checkbox"
                checked={autoEnabled}
                onChange={(e) => setAutoEnabled(e.target.checked)}
              />
              <span className={styles.slider}></span>
            </label>
          </div>

          <div className={styles.formGroup}>
            <label>If balance falls below ($)</label>
            <input
              type="number"
              className={styles.formInput}
              value={autoThreshold}
              onChange={(e) => setAutoThreshold(e.target.value)}
              min="10"
            />
          </div>

          <div className={styles.formGroup}>
            <label>Add to wallet ($)</label>
            <input
              type="number"
              className={styles.formInput}
              value={autoAmount}
              onChange={(e) => setAutoAmount(e.target.value)}
              min="50"
              max="2000"
            />
          </div>

          <div className={styles.formGroup} style={{ borderBottom: 'none' }}>
            <label>Max Refills Per Day</label>
            <select
              className={styles.formInput}
              value={autoMaxDaily}
              onChange={(e) => setAutoMaxDaily(e.target.value)}
            >
              {[1, 2, 3, 4, 5].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>

          {autoTopupConfig?.card_info ? (
            <div className={styles.paymentMethodBox}>
              <FontAwesomeIcon icon={faCreditCard} />{' '}
              {autoTopupConfig.card_info.brand.toUpperCase()} ending in{' '}
              {autoTopupConfig.card_info.last4}
            </div>
          ) : (
            <div
              className={styles.paymentMethodBox}
              style={{ color: '#f5d400', borderColor: '#f5d400' }}
            >
              No payment method saved. Please add one below.
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
            <button
              className={styles.customSubmitBtn}
              onClick={() =>
                saveAutoTopupConfig({
                  enabled: autoEnabled,
                  threshold_usd: autoThreshold,
                  amount_usd: autoAmount,
                  max_daily: autoMaxDaily,
                })
              }
            >
              Save Configuration
            </button>
            <button
              className={styles.customSubmitBtn}
              style={{
                backgroundColor: '#2c3046',
                border: '1px solid #6f9aee',
                color: '#6f9aee',
              }}
              onClick={handleStripeSetup}
              disabled={isStripeLoading}
            >
              {isStripeLoading ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                'Update Card'
              )}
            </button>
          </div>
        </div>
      )}

      <p
        style={{
          color: '#888',
          fontSize: '0.85em',
          textAlign: 'center',
          marginTop: '2rem',
        }}
      >
        Payments are processed securely via Stripe. Your card details never
        touch our servers.
      </p>
    </div>
  );

  const ChartRenderer = ({ type, data, metric }) => {
    if (analyticsDashboardLoading)
      return (
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>Generating Visualization...</p>
        </div>
      );
    if (analyticsDashboardError)
      return (
        <div className={styles.errorContainer}>
          <h4>Error</h4>
          <p>{analyticsDashboardError}</p>
        </div>
      );
    if (!data)
      return (
        <div className={styles.noData}>
          No data available for this selection.
        </div>
      );

    switch (type) {
      case 'pie': {
        const dataset = data.datasets?.[0];
        if (!dataset || !dataset.data || dataset.data.length === 0)
          return <div className={styles.noData}>No data available.</div>;
        const coloredData = {
          ...data,
          datasets: [
            {
              ...dataset,
              backgroundColor: dataset.data.map(
                (_, i) => COLOR_PALETTE[i % COLOR_PALETTE.length],
              ),
              borderColor: '#232532',
              borderWidth: 2,
            },
          ],
        };
        return (
          <Pie options={{ ...chartOptions, scales: {} }} data={coloredData} />
        );
      }
      case 'line':
      case 'bar':
      case 'stacked_bar': {
        if (
          !data.datasets ||
          data.datasets.length === 0 ||
          data.datasets.every((ds) => ds.data.length === 0)
        )
          return <div className={styles.noData}>No data available.</div>;
        const coloredData = {
          ...data,
          datasets: data.datasets.map((dataset, index) => {
            const color = COLOR_PALETTE[index % COLOR_PALETTE.length];
            return {
              ...dataset,
              backgroundColor: type === 'line' ? 'rgba(0,0,0,0)' : color,
              borderColor: color,
              tension: 0.1,
            };
          }),
        };
        return type === 'line' ? (
          <Line options={chartOptions} data={coloredData} />
        ) : (
          <Bar options={chartOptions} data={coloredData} />
        );
      }
      case 'kpi_card': {
        const formattedValue = new Intl.NumberFormat('en-US', {
          style: metric.includes('cost') ? 'currency' : 'decimal',
          currency: 'USD',
          maximumFractionDigits: metric.includes('cost') ? 2 : 0,
        }).format(data.value || 0);
        return (
          <div className={styles.kpiContainer}>
            <div className={styles.kpiValue}>{formattedValue}</div>
            <div className={styles.kpiLabel}>
              {toTitleCase(metric.replace('_', ' '))}
            </div>
          </div>
        );
      }
      default:
        return (
          <div className={styles.noData}>Unsupported chart type: {type}</div>
        );
    }
  };

  if (!isAnalyticsModalOpen) return null;

  return (
    <div
      className={`${styles.modalOverlay} ${isMounted ? styles.show : ''}`}
      onClick={handleClose}
    >
      <div
        className={`${styles.modalContent} ${isMounted ? styles.show : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.modalHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <h3 style={{ margin: 0 }}>Billing & Usage Portal</h3>
            <div className={styles.tabContainer}>
              <button
                className={`${styles.tabButton} ${activeTab === 'usage' ? styles.active : ''}`}
                onClick={() => setActiveTab('usage')}
              >
                <FontAwesomeIcon icon={faChartLine} /> Current Usage
              </button>
              {isAdmin && (
                <>
                  <button
                    className={`${styles.tabButton} ${activeTab === 'topup' ? styles.active : ''}`}
                    onClick={() => setActiveTab('topup')}
                  >
                    <FontAwesomeIcon icon={faWallet} /> Wallet & Top-Up
                  </button>
                  <button
                    className={`${styles.tabButton} ${activeTab === 'transactions' ? styles.active : ''}`}
                    onClick={() => setActiveTab('transactions')}
                  >
                    <FontAwesomeIcon icon={faExchangeAlt} /> Wallet Transactions
                  </button>
                  <button
                    className={`${styles.tabButton} ${activeTab === 'history' ? styles.active : ''}`}
                    onClick={() => setActiveTab('history')}
                  >
                    <FontAwesomeIcon icon={faSyncAlt} /> Invoice History
                  </button>
                </>
              )}
            </div>
          </div>
          <button onClick={handleClose} className={styles.closeButton}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className={styles.modalBody}>
          {activeTab === 'usage' && (
            <div className={styles.dashboardGrid}>
              <div className={styles.controlPanel}>
                <div
                  className={`${styles.walletBalanceCard} ${walletBalance > 10 ? styles.balanceHealthy : walletBalance > 0 ? styles.balanceWarning : styles.balanceCritical}`}
                >
                  <div className={styles.walletBalanceLabel}>
                    Current Workspace Balance
                  </div>
                  <div className={styles.walletBalanceValue}>
                    ${walletBalance.toFixed(2)}
                  </div>
                </div>
                <h4>Build Your View</h4>
                <div className={styles.sentenceBuilder}>
                  <span>Show me</span>
                  <select
                    value={metric}
                    onChange={(e) => setMetric(e.target.value)}
                  >
                    <option value="total_cost">Total Cost</option>
                    <option value="api_calls">API Calls</option>
                    <option value="cost_breakdown">Cost Breakdown</option>
                  </select>
                  <span>by</span>
                  <select
                    value={primaryDimension}
                    onChange={(e) => setPrimaryDimension(e.target.value)}
                  >
                    <option value="">None</option>
                    <option value="analysis_type">Analysis Type</option>
                    <option value="model_name">Model Name</option>
                    <option value="call_outcome">Call Outcome</option>
                    <option value="user_id">User ID</option>
                  </select>
                  {showSecondary && (
                    <>
                      <span>then by</span>
                      <select
                        value={secondaryDimension}
                        onChange={(e) => setSecondaryDimension(e.target.value)}
                      >
                        <option value="">None</option>
                        <option value="analysis_type">Analysis Type</option>
                        <option value="model_name">Model Name</option>
                        <option value="call_outcome">Call Outcome</option>
                        <option value="user_id">User ID</option>
                      </select>
                    </>
                  )}
                  {primaryDimension && !showSecondary && (
                    <button
                      className={styles.addBreakdownButton}
                      onClick={() => setShowSecondary(true)}
                    >
                      + Add Breakdown
                    </button>
                  )}
                  <span>over</span>
                  <select
                    value={timeFrame}
                    onChange={(e) => handleTimeFrameChange(e.target.value)}
                  >
                    <option value="today">Today</option>
                    <option value="this_month">Month to Date</option>
                    <option value="last7days">the Last 7 Days</option>
                    <option value="last30days">the Last 30 Days</option>
                    <option value="custom">a Custom Range...</option>
                  </select>
                  {timeFrame !== 'custom' && timeFrame !== 'today' && (
                    <select
                      value={timeGrouping}
                      onChange={(e) => setTimeGrouping(e.target.value)}
                    >
                      <option value="day">grouped by Day</option>
                      <option value="week">grouped by Week</option>
                      <option value="month">grouped by Month</option>
                    </select>
                  )}
                </div>
                {timeFrame === 'custom' && (
                  <div className={styles.customDateContainer}>
                    <input
                      type="date"
                      value={customTimeRange.start_date}
                      onChange={(e) =>
                        setCustomTimeRange((p) => ({
                          ...p,
                          start_date: e.target.value,
                        }))
                      }
                    />
                    <span>to</span>
                    <input
                      type="date"
                      value={customTimeRange.end_date}
                      onChange={(e) =>
                        setCustomTimeRange((p) => ({
                          ...p,
                          end_date: e.target.value,
                        }))
                      }
                    />
                  </div>
                )}
                <div className={styles.totalAccumulatedBox}>
                  <div className={styles.totalValue}>
                    {metric.includes('cost')
                      ? `$${(Number(visibleTotal) || 0).toFixed(2)}`
                      : Math.round(Number(visibleTotal) || 0)}
                  </div>
                  <div className={styles.totalLabel}>
                    Accumulated {toTitleCase(metric.replace('_', ' '))}
                  </div>
                </div>
              </div>
              <div className={styles.chartContainer}>
                <div className={styles.chartHeader}>
                  <h4 className={styles.chartTitle}>
                    {analyticsDashboardData?.chart_title || 'Analytics'}
                  </h4>
                  <div className={styles.chartTypeSwitcher}>
                    {analyticsDashboardData?.available_chart_types?.map(
                      (type) => (
                        <button
                          key={type}
                          className={`${styles.chartTypeButton} ${chartToRender === type ? styles.active : ''}`}
                          onClick={() => setAnalyticsSelectedChartType(type)}
                        >
                          <FontAwesomeIcon
                            icon={chartIconMap[type] || faChartBar}
                          />
                        </button>
                      ),
                    )}
                  </div>
                </div>
                <div className={styles.chartWrapper}>
                  {unsupportedState.isUnsupported ? (
                    <div className={styles.errorContainer}>
                      <h4>Unsupported View</h4>
                      <p>{unsupportedState.message}</p>
                    </div>
                  ) : (
                    <ChartRenderer
                      type={chartToRender}
                      data={analyticsDashboardData?.data}
                      metric={metric}
                    />
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'topup' && renderTopUp()}

          {activeTab === 'transactions' && (
            <div className={styles.historyContainer}>
              {analyticsDashboardLoading ? (
                <div className={styles.loadingContainer}>
                  <div className={styles.spinner}></div>
                  <p>Fetching transactions...</p>
                </div>
              ) : (
                <table className={styles.historyTable}>
                  <thead>
                    <tr>
                      <th style={{ width: '20%' }}>Date</th>
                      <th style={{ width: '20%' }}>Type</th>
                      <th style={{ width: '15%' }}>Amount</th>
                      <th style={{ width: '20%' }}>User</th>
                      <th style={{ width: '25%' }}>Ref / Intent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Mode: Transactions Table */}
                    {companyWalletTransactions.map((tx) => {
                      const isUsage = tx.transaction_type === 'LLM_USAGE';
                      const isAuto =
                        tx.transaction_type === 'AUTO_STRIPE_TOP_UP';
                      const localDisplay = formatToLocalTime(tx.created_at);

                      return (
                        <tr key={tx.transactionId} className={styles.tableRow}>
                          {/* 1. Date with secondary time */}
                          <td>
                            <div
                              style={{
                                fontSize: '0.9rem',
                                fontWeight: '500',
                                color: '#ffffff',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {localDisplay.date}
                            </div>
                            <div
                              style={{
                                fontSize: '0.75rem',
                                color: '#777',
                                marginTop: '1px',
                              }}
                            >
                              {localDisplay.time}
                            </div>
                          </td>

                          {/* 2. Enhanced Badge with Icons */}
                          <td>
                            <span
                              className={`${styles.txBadge} ${styles[tx.transaction_type]}`}
                            >
                              <FontAwesomeIcon
                                icon={
                                  isAuto
                                    ? faRobot
                                    : isUsage
                                      ? faCogs
                                      : faCreditCard
                                }
                                size="xs"
                              />
                              {tx.transaction_type.replace(/_/g, ' ')}
                            </span>
                          </td>

                          {/* 3. Color-coded Amount */}
                          <td
                            className={
                              isUsage ? styles.amtNegative : styles.amtPositive
                            }
                          >
                            {isUsage ? '-' : '+'}$
                            {Math.abs(parseFloat(tx.amount_usd)).toFixed(2)}
                          </td>

                          {/* 4. User with Avatar Icon */}
                          <td>
                            <div className={styles.userCell}>
                              <FontAwesomeIcon
                                icon={
                                  tx.userName === 'System' ? faRobot : faUser
                                }
                                style={{ opacity: 0.5 }}
                                size="sm"
                              />
                              <span>{tx.userName || 'System'}</span>
                            </div>
                          </td>

                          {/* 5. Shortened Reference ID */}
                          <td>
                            <span
                              className={styles.intentText}
                              title={tx.stripe_payment_intent}
                            >
                              {tx.stripe_payment_intent
                                ? `${tx.stripe_payment_intent.substring(0, 8)}...${tx.stripe_payment_intent.slice(-4)}`
                                : 'N/A'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div className={styles.historyContainer}>
              {analyticsDashboardLoading ? (
                <div className={styles.loadingContainer}>
                  <div className={styles.spinner}></div>
                  <p>Fetching records...</p>
                </div>
              ) : (
                <table className={styles.historyTable}>
                  <thead>
                    <tr>
                      <th style={{ width: '12%' }}>Status</th>
                      <th style={{ width: '15%' }}>Month</th>
                      <th style={{ width: '15%' }}>Amount</th>
                      <th style={{ width: '18%' }}>Issued</th>
                      <th style={{ width: '18%' }}>Paid On</th>
                      <th style={{ width: '22%' }}>Payment Link</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userInvoices.map((inv) => (
                      <tr key={inv.invoiceId}>
                        <td>
                          <span
                            className={`${styles.statusBadge} ${inv.status === 'PAID' ? styles.paid : styles.unpaid}`}
                          >
                            {inv.status}
                          </span>
                        </td>
                        <td>{inv.billing_month}</td>
                        <td>${inv.amount_usd}</td>
                        <td>
                          {inv.created_at
                            ? new Date(inv.created_at).toLocaleDateString()
                            : '-'}
                        </td>
                        <td>
                          {inv.paid_at
                            ? new Date(inv.paid_at).toLocaleDateString()
                            : '-'}
                        </td>
                        <td>
                          {inv.status === 'UNPAID' ? (
                            <a
                              href={inv.stripe_payment_link}
                              target="_blank"
                              rel="noreferrer"
                              className={styles.payLink}
                            >
                              Pay via Stripe{' '}
                              <FontAwesomeIcon
                                icon={faExternalLinkAlt}
                                size="xs"
                              />
                            </a>
                          ) : (
                            <span style={{ color: '#69f0ae' }}>Verified</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboardModal;
